import { useFeature } from '@growthbook/growthbook-react';
import { useMemo, useCallback } from 'react';
import { useSelector } from 'store';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import { RecordFilterType } from 'store/slices/recordFilter';
import { FeatureFlags } from 'types';
import {
  QueryParams,
  QueryFilterOperators,
  GenericResource,
  NestedTypeField,
} from 'types/api';

export const useRecordFilter = <T extends GenericResource>({
  userProperty = 'createdBy',
  teamProperty = 'team',
  mineIsNotApplicable = false,
}: {
  userProperty?: string;
  teamProperty?: string;
  mineIsNotApplicable?: boolean;
}): {
  filters: QueryParams<T>['filters'];
  filterRecords: (records: T[]) => T[];
} => {
  const filterState = useSelector((state) => state.recordFilter);
  const { data: user } = useGetUserQuery();
  const tempMineAllFilter = useFeature(FeatureFlags.tempMineAllFilter).on;

  const filters = useMemo((): QueryParams<T>['filters'] => {
    if (!tempMineAllFilter) {
      return [];
    }

    if (filterState.filterType === RecordFilterType.AllRecords) {
      return [];
    }

    if (
      mineIsNotApplicable &&
      filterState.filterType === RecordFilterType.MyRecords
    ) {
      return [];
    }

    if (filterState.filterType === RecordFilterType.MyRecords) {
      if (!user?.id) {
        return [];
      }
      return [
        {
          field: userProperty as NestedTypeField<T>,
          operator: QueryFilterOperators['='],
          value: user.id,
        },
      ] as QueryParams<T>['filters'];
    }

    if (filterState.filterType === RecordFilterType.MyTeamsRecords) {
      if (!filterState.selectedTeams.length) {
        return [];
      }
      return [
        {
          field: teamProperty as NestedTypeField<T>,
          operator: QueryFilterOperators.isAnyOf,
          value: filterState.selectedTeams,
        },
      ] as QueryParams<T>['filters'];
    }

    return [];
  }, [
    filterState.filterType,
    filterState.selectedTeams,
    user?.id,
    tempMineAllFilter,
    userProperty,
    teamProperty,
  ]);

  const filterRecords = useCallback(
    (records: T[]): T[] => {
      if (!tempMineAllFilter) {
        return records;
      }

      if (filterState.filterType === RecordFilterType.AllRecords) {
        return records;
      }

      if (
        mineIsNotApplicable &&
        filterState.filterType === RecordFilterType.MyRecords
      ) {
        return records;
      }

      if (filterState.filterType === RecordFilterType.MyRecords) {
        if (!user?.id) {
          return records;
        }
        return records.filter(
          (record) =>
            (record as { createdBy?: { id?: string } | null }).createdBy?.id ===
            user.id
        );
      }

      if (filterState.filterType === RecordFilterType.MyTeamsRecords) {
        if (!filterState.selectedTeams.length) {
          return records;
        }
        return records.filter((record) => {
          const team = (record as { team?: { id?: string } | string | null })
            .team;
          if (!team) {
            return false;
          }
          const teamId = typeof team === 'string' ? team : team.id;
          return teamId && filterState.selectedTeams.includes(teamId);
        });
      }

      return records;
    },
    [
      filterState.filterType,
      filterState.selectedTeams,
      user?.id,
      tempMineAllFilter,
      mineIsNotApplicable,
      userProperty,
      teamProperty,
    ]
  );

  return { filters, filterRecords };
};
